/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { History } from '../../_helpers/history'
import { handleMessages } from '../../_helpers/utilities'
import {
  ICustomerDetailsFilters,
  ICustomerListFilters,
  ICustomerManagementState,
  IDeleteIds,
} from '../../_models/superAdmin/customerManagementInterface'
import customerManagementService from '../../_services/superAdmin/customerManagementService'
import { RootState } from '../../_store/store'
import { startLoader, stopLoader } from '../loaderSlice'
import { errorToast, successToast } from '../toastSlice'

const initialState: ICustomerManagementState = {
  customerList: {
    current_page: 1,
    data: [],
    total: 20,
    per_page: 10,
    last_page: 0,
  },
  kpis: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  customerListFilters: {
    page: 1,
    per_page: 10,
    search: '',
    order: 'asc',
    // Advanced search fields
    name: '',
    email: '',
    phone: '',
    country_code: '',
    status_active: false,
    status_inactive: false,
    created_from: '',
    created_to: '',
    login_from: '',
    login_to: '',
  },
  isLoadingCustomerList: false,
  customerDetails: {
    id: null,
    name: '',
    email: '',
    phone: '',
    address: '',
    created_at: '',
    status: '',
    country_code: '',
  },
  customerKpis: {
    total_bookings: 0,
    last_login: '',
  },
  bookings: {
    current_page: 1,
    data: [],
    total: 0,
    per_page: 10,
    last_page: 0,
  },
  bookingListFilters: {
    page: 1,
    per_page: 10,
    customer_id: null,
    order: 'desc',
    search: '',
  },
  searchChipsStatus: false,
  filterApplied: false,
  isLoadingCustomerDetails: false,
  isLastUserPage: false,
  isDeleteModalOpen: false,
  customerIdsToDelete: [],
  selectedCellIds: [],
}

export const getCustomerList = createAsyncThunk(
  '/getCustomerList',
  async (filters: ICustomerListFilters, { dispatch, rejectWithValue }) => {
    try {
      const response = await customerManagementService.getCustomerList(filters)

      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getCustomerDetails = createAsyncThunk(
  '/getCustomerDetails',
  async (filters: ICustomerDetailsFilters, { dispatch, rejectWithValue }) => {
    try {
      const response = await customerManagementService.getCustomerDetails(filters)
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const deleteCustomer = createAsyncThunk(
  '/deleteCustomer',
  async (ids: IDeleteIds, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())

    try {
      const response = await customerManagementService.deleteCustomer(ids)
      dispatch(stopLoader())
      dispatch(closeCustomerDeleteModal())
      dispatch(successToast(handleMessages(response.message)))
      const { customerManagement } = getState() as { customerManagement: ICustomerManagementState }
      if (customerManagement.isLastUserPage) {
        const isDeletingAllCustomersOnPage =
          customerManagement.customerList.data.length > 0 &&
          ids.customer_id.length === customerManagement.customerList.data.length

        if (isDeletingAllCustomersOnPage) {
          const currentPage = customerManagement?.customerListFilters?.page ?? 1
          const previousPage = Math.max(1, currentPage - 1)
          dispatch(
            getCustomerList({
              ...customerManagement.customerListFilters,
              page: previousPage,
            }),
          )
        } else {
          dispatch(getCustomerList(customerManagement.customerListFilters))
        }
      } else {
        dispatch(getCustomerList(customerManagement.customerListFilters))
      }
      History.navigate('/super-admin/customers')
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const customerManagementSlice = createSlice({
  name: 'superAdminCustomer',
  initialState,
  reducers: {
    changeFilters: (state, action) => {
      state.customerListFilters = { ...state.customerListFilters, ...action.payload }
    },
    changeBookingListFilters: (state, action) => {
      state.bookingListFilters = { ...state.bookingListFilters, ...action.payload }
    },
    resetBookingListFilters: (state) => {
      state.bookingListFilters = initialState.bookingListFilters
    },
    setFilters: (state, action) => {
      state.customerListFilters = {
        ...state.customerListFilters,
        ...action.payload,
      }
    },
    resetFilters: (state) => {
      state.customerListFilters = initialState.customerListFilters
    },
    showSearchChips: (state) => {
      state.searchChipsStatus = true
    },
    hideSearchChips: (state) => {
      state.searchChipsStatus = false
    },
    filterAppliedStatus: (state, action) => {
      state.filterApplied = action.payload
    },
    resetCustomerListData: (state) => {
      state.customerList = initialState.customerList
    },
    openCustomerDeleteModal: (state) => {
      state.isDeleteModalOpen = true
    },
    closeCustomerDeleteModal: (state) => {
      state.isDeleteModalOpen = false
    },
    setCustomerIdsToDelete: (state, action) => {
      state.customerIdsToDelete = action.payload
    },
    setSelectedCellIds: (state, action) => {
      state.selectedCellIds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomerList.fulfilled, (state, action) => {
      state.customerList = action.payload.data.customers

      state.customerListFilters.page = state.customerList.current_page
      state.kpis = action.payload.data.kpis
      state.isLoadingCustomerList = false
      if (
        state.customerList.current_page === state.customerList.last_page &&
        state.customerList.last_page !== 1
      ) {
        state.isLastUserPage = true
      } else {
        state.isLastUserPage = false
      }
    })
    builder.addCase(getCustomerList.pending, (state) => {
      state.isLoadingCustomerList = true
    })
    builder.addCase(getCustomerList.rejected, (state) => {
      state.isLoadingCustomerList = false
    })
    builder.addCase(getCustomerDetails.pending, (state) => {
      state.isLoadingCustomerDetails = true
      state.bookings = initialState.bookings
    })
    builder.addCase(getCustomerDetails.fulfilled, (state, action) => {
      state.customerDetails = action.payload.data.customer
      state.customerKpis = action.payload.data.kpi
      state.bookings = action.payload.data.bookings
      state.isLoadingCustomerDetails = false
    })
    builder.addCase(getCustomerDetails.rejected, (state) => {
      state.isLoadingCustomerDetails = false
    })
    builder.addCase(deleteCustomer.fulfilled, (state) => {
      state.customerIdsToDelete = []
      state.selectedCellIds = []
    })
  },
})

export const {
  changeFilters,
  changeBookingListFilters,
  setFilters,
  resetFilters,
  showSearchChips,
  hideSearchChips,
  filterAppliedStatus,
  resetBookingListFilters,
  resetCustomerListData,
  openCustomerDeleteModal,
  closeCustomerDeleteModal,
  setCustomerIdsToDelete,
  setSelectedCellIds,
} = customerManagementSlice.actions
export const customerManagementState = (state: RootState) => state.customerManagement
const customerManagementReducer = customerManagementSlice.reducer
export default customerManagementReducer
